import React, { useState, useEffect } from "react";
import { Container, Col, Row, Form, Table, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchSalaryReport, clearSalaryReport } from "../../slice/salarySilce";
import axiosInstance from "../../config/API";
import { toast } from "react-toastify";

const ReportAdvance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const fromDate = queryParams.get("from_date");
  const toDate = queryParams.get("to_date");
  const selectedStaffType = queryParams.get("staff_type") || "";
  const selectedProduct = queryParams.get("product") || "";

  const { SalaryReport } = useSelector((state) => state.SalaryReportSlice);
  const [deductions, setDeductions] = useState({});
  const [isExistingReport, setIsExistingReport] = useState(false);
  
  // Hit the API on page load with the passed filters
  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(fetchSalaryReport({ from_date: fromDate, to_date: toDate }));
    }
  }, [dispatch, fromDate, toDate]);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const response = await axiosInstance.post("/report.php", {
          action: "listWeeklySalary"
        });
        if (response.data.head.code === 200) {
          const existing = response.data.body.weekly_salary.some(
            ws => ws.from_date === fromDate && ws.to_date === toDate
          );
          setIsExistingReport(existing);
        }
      } catch (err) {
        console.error("Failed to check existing salary", err);
      }
    };

    if (fromDate && toDate) {
      checkExisting();
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (SalaryReport && SalaryReport.length > 0) {
      const initialDeductions = {};
      
      if (isExistingReport) {
        const fetchSaved = async () => {
          const res = await axiosInstance.post("/report.php", { action: "listWeeklySalary" });
          const savedEntry = res.data.body.weekly_salary.find(
            ws => ws.from_date === fromDate && ws.to_date === toDate
          );
          if (savedEntry) {
            const salaryData = savedEntry.salary_data;
            salaryData.forEach(item => {
              if (item.deduction > 0) {
                initialDeductions[item.staff_name] = item.deduction;
              }
            });
            setDeductions(initialDeductions);
          }
        };
        fetchSaved();
      } else {
        SalaryReport.forEach(item => {
          if (item.staff_advance > 0) {
            initialDeductions[item.staff_name] = 0;
          }
        });
        setDeductions(initialDeductions);
      }
    }
  }, [SalaryReport, isExistingReport, fromDate, toDate]);

  const handleDeductionChange = (staffName, value) => {
    setDeductions((prev) => ({ ...prev, [staffName]: value }));
  };

  const handleSubmitWeeklySalary = async () => {
    const salaryDataArray = Object.entries(transformedData).map(([staffName, data]) => ({
      staff_name: staffName,
      // Added staff_advance here to be saved in the database
      staff_advance: staffTotals[staffName].advance, 
      total_earnings: staffTotals[staffName].total_value,
      deduction: parseFloat(deductions[staffName] || 0),
      net_payable: staffTotals[staffName].total_value - parseFloat(deductions[staffName] || 0),
      breakdown: data
    }));

    const payload = {
      action: "createWeeklySalary",
      from_date: fromDate,
      to_date: toDate,
      data: salaryDataArray
    };

    try {
      const response = await axiosInstance.post("/report.php", payload);
      if (response.data.head.code === 200) {
        toast.success(response.data.head.msg || "Weekly Salary Recorded Successfully!");
        dispatch(clearSalaryReport());
        setTimeout(() => {
          navigate("/report");
        }, 1500);
      } else {
        toast.error(response.data.head.msg || "Error recording salary");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to connect to the server. Please try again.");
    }
  };

  const filteredData = SalaryReport?.map((entry) => ({
    ...entry,
    products: entry.products.filter(
      (p) =>
        (selectedStaffType ? entry.staff_type === selectedStaffType : true) &&
        (selectedProduct ? p.product_name === selectedProduct : true)
    ),
  })).filter((entry) => entry.products.length > 0);

  const transformData = (data) => {
    let transformed = {};
    let uniqueDates = new Set();
    let grandTotal = { total_value: 0 };
    let staffTotals = {};

    data.forEach((entry) => {
      const formattedDate = new Date(entry.entry_date).toLocaleDateString("en-GB");
      uniqueDates.add(formattedDate);
      if (!staffTotals[entry.staff_name]) {
        staffTotals[entry.staff_name] = { 
          total_value: 0, 
          advance: entry.staff_advance || 0 
        };
      }

      entry.products.forEach((product) => {
        const staffKey = entry.staff_name;
        const typeKey = `${entry.staff_name}-${entry.staff_type}`;
        const productKey = `${entry.staff_name}-${entry.staff_type}-${product.product_name}`;

        if (!transformed[staffKey]) transformed[staffKey] = {};
        if (!transformed[staffKey][typeKey]) transformed[staffKey][typeKey] = { staff_type: entry.staff_type, products: {} };
        if (!transformed[staffKey][typeKey].products[productKey]) {
          transformed[staffKey][typeKey].products[productKey] = {
            product_name: product.product_name,
            per_cooly_rate: product.per_cooly_rate,
            total: 0,
            total_count: 0,
            counts: {},
          };
        }
        transformed[staffKey][typeKey].products[productKey].counts[formattedDate] = (transformed[staffKey][typeKey].products[productKey].counts[formattedDate] || 0) + product.count;
        transformed[staffKey][typeKey].products[productKey].total += product.total;
        transformed[staffKey][typeKey].products[productKey].total_count += product.count;
        staffTotals[staffKey].total_value += product.total;
        grandTotal.total_value += product.total;
      });
    });

    return {
      transformedData: transformed,
     uniqueDates: Array.from(uniqueDates).sort((a, b) => {
            const [dayA, monthA, yearA] = a.split("/").map(Number);
            const [dayB, monthB, yearB] = b.split("/").map(Number);
            return (
              new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB)
            );
          }),
      grandTotal,
      staffTotals,
    };
  };

  const { transformedData, uniqueDates, grandTotal, staffTotals } = transformData(filteredData || []);

  return (
    <div id="main">
      <Container>
        <Row className="mb-3 text-center">
          <Col>
            <h3>SRI GURU LAKSHMI FIREWORKS</h3>
            <h6>Weekly Salary Payment Processing</h6>
            <p className="text-muted">For Period: {fromDate} to {toDate}</p>
          </Col>
        </Row>

        <Table bordered responsive>
          <thead>
            <tr className="table-light">
              <th>Staff Name</th>
              <th>Staff Advance</th><th>Staff Type</th><th>Product Name</th>
              {uniqueDates.map((date) => <th key={date}>{date}</th>)}
              <th>Total Count</th><th>Rate</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(transformedData).map(([staffName, staffData]) => (
              <React.Fragment key={staffName}>
                {Object.entries(staffData).map(([typeKey, typeData], tIdx) => (
                  Object.values(typeData.products).map((product, pIdx) => (
                    <tr key={`${typeKey}-${pIdx}`}>
                      {tIdx === 0 && pIdx === 0 && (
                        <>
                          <td rowSpan={Object.values(staffData).reduce((acc, td) => acc + Object.values(td.products).length, 0)} className="fw-bold align-middle">
                            {staffName}
                          </td>
                          <td rowSpan={Object.values(staffData).reduce((acc, td) => acc + Object.values(td.products).length, 0)} className="align-middle fw-bold">
                            {staffTotals[staffName].advance.toFixed(2)}
                          </td>
                        </>
                      )}
                      {pIdx === 0 && <td rowSpan={Object.values(typeData.products).length} className="align-middle">{typeData.staff_type}</td>}
                      <td>{product.product_name}</td>
                      {uniqueDates.map(d => <td key={d}>{product.counts[d] || "-"}</td>)}
                      <td>{product.total_count}</td><td>{product.per_cooly_rate}</td><td>{product.total.toFixed(2)}</td>
                    </tr>
                  ))
                ))}
                <tr className="table-info fw-bold">
                  <td colSpan={6 + uniqueDates.length} className="text-end">{staffName} Total:</td>
                  <td>{staffTotals[staffName].total_value.toFixed(2)}</td>
                </tr>
                <tr style={{ backgroundColor: "#fff4e6" }}>
                  <td colSpan={6 + uniqueDates.length} className="text-end text-danger fw-bold">Deduction Amount :</td>
                  <td>
                    <Form.Control 
                      type="text" 
                      size="sm" 
                      placeholder="0.00"
                      disabled={isExistingReport || staffTotals[staffName].advance <= 0}
                      value={deductions[staffName] || ""} 
                      onChange={(e) => {
                        if (isExistingReport) return;
                        const val = parseFloat(e.target.value) || 0;
                        const earnings = staffTotals[staffName].total_value;
                        const availableAdvance = staffTotals[staffName].advance;
                        const maxLimit = Math.min(earnings, availableAdvance);

                        if (val <= maxLimit) {
                          handleDeductionChange(staffName, e.target.value);
                        } else {
                          handleDeductionChange(staffName, maxLimit.toString());
                          toast.error(`Limit reached: ${maxLimit.toFixed(2)}`);
                        }
                      }} 
                    />
                  </td>
                </tr>
                <tr className=" fw-bold">
                  <td colSpan={6 + uniqueDates.length} className="text-end text-success">Net Balance :</td>
                  <td>
                    {(staffTotals[staffName].total_value - parseFloat(deductions[staffName] || 0)).toFixed(2)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            <tr className=" fw-bold">
              <td colSpan={7 + uniqueDates.length} className=" text-center align-middle  " style={{ backgroundColor: 'rgb(41, 100, 175)', color: 'white' }}>
                <span className="me-4">Grand Total: {grandTotal.total_value.toFixed(2)}</span>
                <span className="me-4">Total Deductions: {Object.values(deductions).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0).toFixed(2)} </span>
                <span>Total Net Balance: {(grandTotal.total_value - Object.values(deductions).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0)).toFixed(2)}</span>
              </td>
            </tr>
          </tbody>
        </Table>

        {!isExistingReport && (
          <Row className="mt-4 pb-5 justify-content-center">
            <Col md="auto">
              <Button variant="success" className="px-5 me-3" onClick={handleSubmitWeeklySalary}>
                Submit Weekly Salary
              </Button>
              <Button variant="secondary" className="px-5" onClick={() => navigate("/report")}>
                Cancel
              </Button>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ReportAdvance;